import { API_URL } from "@/config/constants";
import { buildUrl } from "@/utils";
import { queryOptions } from "@tanstack/react-query";
import axios from "axios";
import { Page } from "../api.types";
import { signalOnceCached } from "../querySignal";
import {
    IChatConversation,
    IChatConversationCreate,
    IChatConversationDetail,
    IChatConversationListItem,
    IChatConversationRequest,
    IChatConversationUpdate,
    IChatRecoveryStatus,
    IConversationsFilter,
    IConversationTitleGenerationRequest,
    IDirectDocumentBudget,
    OpenChatStream,
} from "./chat.types";
import {
    ASSISTANT_MESSAGE_ID_HEADER,
    parseAssistantMessageIdHeader,
} from "./turn-handoff";

const API = `${API_URL}/chat`;

const conversationPath = (id: number | string) =>
    `${API}/conversations/${encodeURIComponent(id)}`;

export interface IChatRequest {
    question: string;
}

export const createConversation = async (
    conversationCreate: IChatConversationCreate
) => {
    const response = await axios.post<IChatConversation>(
        `${API}/conversations`,
        conversationCreate
    );
    return response.data;
};

export const getConversations = async (
    page: number = 1,
    size: number = 50,
    filter?: IConversationsFilter,
    clientId?: number,
    includeShared?: boolean,
    { signal }: { signal?: AbortSignal } = {}
) => {
    const url = buildUrl(`${API}/conversations`, {
        page,
        size,
        ...filter,
        // Omission (not null) = unscoped; mirrors client_id at create.
        ...(clientId !== undefined && { client_id: clientId }),
        // Opt in to collaborator rows (shared WITH the requester). Omitted =
        // owner-only (the endpoint default), which the Polly sidebar and home
        // Recents widget rely on to avoid double-sourcing the Shared group.
        ...(includeShared && { include_shared: true }),
    });

    const response = await axios.get<Page<IChatConversationListItem>>(url, {
        signal,
    });
    return response.data;
};

export const getConversation = async (
    id: number | string,
    { signal }: { signal?: AbortSignal } = {}
) => {
    const response = await axios.get<IChatConversationDetail>(
        conversationPath(id),
        { signal }
    );
    return response.data;
};

export const conversationDetailQueryKey = (id: string) =>
    ["conversations", id] as const;

export const conversationDetailQueryOptions = (id: string) =>
    queryOptions({
        queryKey: conversationDetailQueryKey(id),
        queryFn: (context) =>
            getConversation(id, { signal: signalOnceCached(context) }),
        staleTime: 60_000,
        refetchOnWindowFocus: false,
    });

export const getDirectDocumentBudget = async (id: number | string) => {
    const response = await axios.get<IDirectDocumentBudget>(
        `${conversationPath(id)}/direct-document-budget`
    );
    return response.data;
};

export const getConversationTitle = async (id: number | string) => {
    const response = await axios.get<{ title: string }>(
        `${conversationPath(id)}/title`
    );
    return response.data.title;
};

export const updateConversation = async (
    id: number | string,
    conversationUpdate: IChatConversationUpdate
) => {
    const response = await axios.put<IChatConversation>(
        conversationPath(id),
        conversationUpdate
    );
    return response.data;
};

export const deleteConversation = async (id: number | string) => {
    const response = await axios.delete(conversationPath(id));
    return response.data;
};

async function toOpenChatStream(
    stream: ReadableStream<Uint8Array>,
    headerValue: unknown,
    status: number,
    signal?: AbortSignal
): Promise<OpenChatStream> {
    if (
        status === 200 &&
        headerValue === undefined &&
        (await hasCleanEmptyBody(stream, signal))
    ) {
        throw new DOMException("The chat turn was superseded", "AbortError");
    }
    let assistantMessageId: number;
    try {
        assistantMessageId = parseAssistantMessageIdHeader(headerValue);
    } catch (error) {
        await stream.cancel(error).catch(() => undefined);
        throw error;
    }
    return {
        stream,
        assistantMessageId,
    };
}

async function hasCleanEmptyBody(
    stream: ReadableStream<Uint8Array>,
    signal?: AbortSignal
): Promise<boolean> {
    const reader = stream.getReader();
    let abortRead: (() => void) | undefined;
    const aborted = signal
        ? new Promise<never>((_, reject) => {
              abortRead = () => {
                  const reason =
                      signal.reason ??
                      new DOMException(
                          "The operation was aborted",
                          "AbortError"
                      );
                  void reader.cancel(reason).catch(() => undefined);
                  reject(reason);
              };
              if (signal.aborted) abortRead();
              else signal.addEventListener("abort", abortRead, { once: true });
          })
        : undefined;
    try {
        while (true) {
            const read = reader.read();
            const { done, value } = aborted
                ? await Promise.race([read, aborted])
                : await read;
            if (done) return true;
            if (value.byteLength > 0) {
                void reader.cancel().catch(() => undefined);
                return false;
            }
        }
    } finally {
        if (abortRead) signal?.removeEventListener("abort", abortRead);
        reader.releaseLock();
    }
}

export const chatCompletion = async (
    conversationId: number | string,
    chatRequest: IChatConversationRequest,
    signal?: AbortSignal
): Promise<OpenChatStream> => {
    const response = await axios.post<ReadableStream<Uint8Array>>(
        `${conversationPath(conversationId)}/completion`,
        chatRequest,
        {
            responseType: "stream",
            adapter: "fetch",
            signal,
        }
    );

    return toOpenChatStream(
        response.data,
        response.headers?.[ASSISTANT_MESSAGE_ID_HEADER.toLowerCase()],
        response.status,
        signal
    );
};

/**
 * Replay the turn that produced `assistantMessageId`.
 *
 * No request body: the server replays the preceding user message itself, deletes
 * the targeted assistant message and everything after it, and streams a fresh
 * answer on the same NDJSON event contract as `/completion`. The ASSISTANT
 * message id is the public target.
 */
export const retryAssistantMessage = async (
    conversationId: number | string,
    assistantMessageId: number | string,
    signal?: AbortSignal
): Promise<OpenChatStream> => {
    const response = await axios.post<ReadableStream<Uint8Array>>(
        `${conversationPath(conversationId)}/messages/${encodeURIComponent(assistantMessageId)}/retry`,
        undefined,
        {
            responseType: "stream",
            adapter: "fetch",
            signal,
        }
    );

    return toOpenChatStream(
        response.data,
        response.headers?.[ASSISTANT_MESSAGE_ID_HEADER.toLowerCase()],
        response.status,
        signal
    );
};

/**
 * Watch a turn that is already running server-side.
 *
 * Read-only: it starts nothing and sends no prompt. The response is the same
 * NDJSON record contract `/completion` and `/retry` stream, so the caller feeds
 * it to the same reducer.
 */
export const openAttachStream = async (
    conversationId: number | string,
    assistantMessageId: number | string,
    signal?: AbortSignal
) => {
    const response = await axios.get(
        `${conversationPath(conversationId)}/messages/${encodeURIComponent(assistantMessageId)}/stream`,
        {
            responseType: "stream",
            adapter: "fetch",
            signal,
        }
    );

    return response.data;
};

// `keepalive` survives an unloading tab only on the fetch adapter, and axios
// defaults to ['xhr', 'http', 'fetch'] — selecting it explicitly is what makes
// the option anything other than inert.
export const abandonActiveChatTurn = async (
    conversationId: number | string,
    assistantMessageId: number
) => {
    await axios.post(
        `${conversationPath(conversationId)}/messages/${encodeURIComponent(assistantMessageId)}/abandon`,
        undefined,
        {
            adapter: "fetch",
            fetchOptions: { keepalive: true },
        }
    );
};

export const abandonActiveConversationTurn = async (
    conversationId: number | string
) => {
    await axios.post(`${conversationPath(conversationId)}/abandon`, undefined, {
        adapter: "fetch",
        fetchOptions: { keepalive: true },
    });
};

export const generateConversationTitle = async (
    conversationId: number | string,
    titleRequest: IConversationTitleGenerationRequest
) => {
    const response = await axios.post<IChatConversation>(
        `${conversationPath(conversationId)}/title`,
        titleRequest
    );
    return response.data;
};

/**
 * Idempotent dismiss of a message's context_enrichment block. Server flips
 * `dismissed=true` and (on transition) increments the conversation's
 * enrichment_dismissed_count. Returns a sentinel `{ dismissed: true }`;
 * the backend keeps 200 (not 204).
 */
export const dismissEnrichment = async (
    conversationId: number | string,
    messageId: number | string
) => {
    const response = await axios.post<{ dismissed: true }>(
        `${conversationPath(conversationId)}/messages/${encodeURIComponent(messageId)}/dismiss-enrichment`
    );
    return response.data;
};

export interface IRecoveryStatusRead {
    /** Null when the server answered 304 -- the caller keeps what it holds. */
    status: IChatRecoveryStatus | null;
    etag: string | null;
}

/**
 * Read one message's recovery status, revalidating with `If-None-Match` when a
 * validator is held. The validator is opaque: it is echoed verbatim and never
 * parsed.
 */
export const getMessageRecoveryStatus = async (
    conversationId: number | string,
    messageId: number | string,
    etag?: string | null
): Promise<IRecoveryStatusRead> => {
    const response = await axios.get<IChatRecoveryStatus>(
        `${conversationPath(conversationId)}/messages/${encodeURIComponent(messageId)}/recovery-status`,
        {
            headers: etag ? { "If-None-Match": etag } : undefined,
            // 304 is a successful revalidation, not an error.
            validateStatus: (code) =>
                code === 304 || (code >= 200 && code < 300),
        }
    );
    const nextEtag = response.headers?.etag ?? null;
    if (response.status === 304) {
        return { status: null, etag: nextEtag ?? etag ?? null };
    }
    return { status: response.data, etag: nextEtag };
};
